import React, { FC, useEffect, useState } from 'react';
import { Region } from 'types/orderTypes';
import './ShiftPlanner.scss';
import dayjs, { Dayjs } from 'dayjs';
import ShiftCalendar from './ShiftCalendar';
import {
  CourierVehicleType,
  handleError,
  JobType,
  ShiftCount,
  ShiftOverviewStatus,
  ShiftsByDate,
} from './utils';
import {
  duplicateCourierShifts,
  fetchCouriersEfficiency,
  fetchDaysUntilCalculation,
  markShiftsPublished,
} from './shiftApi';
import { ButtonComponent } from 'ReusableComponents/Button/ButtonComponent';
import {
  PlannerDates,
  ShiftFilterOptions,
  TransportOverview,
} from './smallerComponents';
import { useShiftCouriers } from './hooks';
import { ShiftStatusType } from '../../const/ShiftConst';
import { ReqEnum } from '../../const/requestStatus';
import { Loader } from '../../components/Loader/Loader';
import Swal from 'sweetalert2';

export const ShiftPlannerContext = React.createContext<{
  shiftCount: ShiftCount;
  startDate: Dayjs;
  endDate: Dayjs;
  daysUntilCalculation: number;
  region: Region;
  couriersEfficiency: Record<string, number>;
} | null>(null);

const ShiftPlanner: FC<{ region: Region }> = ({ region }) => {
  const [startDate, setStartDate] = useState<Dayjs>(
    dayjs().day(1).startOf('day')
  );
  const [endDate, setEndDate] = useState<Dayjs>(dayjs().day(7).startOf('day'));

  const [selectedJobType, setSelectedJobType] = useState<JobType>(JobType.ALL);
  const [selectedShiftStatus, setSelectedShiftStatus] =
    useState<ShiftOverviewStatus>(ShiftOverviewStatus.ALL);
  const [selectedCarType, setSelectedCarType] = useState<CourierVehicleType>(
    CourierVehicleType.ALL
  );

  const [onlyExtraWork, setOnlyExtraWork] = useState<boolean>(false);

  const [nameSearch, setNameSearch] = useState<string>('');
  const [daysUntilCalculation, setDaysUntilCalculation] = useState<number>(0);
  const [shiftsToPublish, setShiftsToPublish] = useState<number>(0);

  const { displayedShifts, displayedCouriers, fetchStatus } = useShiftCouriers(
    startDate,
    endDate,
    selectedJobType,
    onlyExtraWork,
    region
  );

  const [filteredShifts, setFilteredShifts] = useState<ShiftsByDate>(
    displayedShifts.byDate
  );

  const [couriersEfficiency, setCouriersEfficiency] = useState<
    Record<string, number>
  >({});

  const publishShifts = () => {
    const shiftsToPublish = Object.values(displayedShifts.currentShifts)
      .filter(shift => !shift.published)
      .map(shift => shift.id);

    markShiftsPublished(shiftsToPublish).catch(handleError);
  };

  const copyToNextWeek = () => {
    const datesCouriers: Record<string, number[]> = {};
    displayedShifts.currentShifts
      .filter(shift => shift.status !== ShiftStatusType.OPEN)
      .forEach(shift => {
        const newDate = dayjs(new Date(shift.date))
          .add(1, 'week')
          .toISOString();

        datesCouriers[newDate] = datesCouriers[newDate]
          ? [...datesCouriers[newDate], shift.courierId]
          : [shift.courierId];
      });

    duplicateCourierShifts(datesCouriers)
      .then(() =>
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'Shifts duplicated!',
          showConfirmButton: false,
          timer: 1500,
        })
      )
      .catch(handleError);
  };

  useEffect(() => {
    fetchDaysUntilCalculation().then(daysCount =>
      setDaysUntilCalculation(daysCount)
    );
  }, []);

  useEffect(() => {
    // Filter shifts by search
    let filtered: ShiftsByDate = displayedShifts.byDate;
    if (nameSearch.length !== 0) {
      const filteredByName: ShiftsByDate = {};
      Object.entries(displayedShifts.byDate).forEach(([date, shifts]) => {
        filteredByName[date] = shifts.filter(shift => {
          const courierName =
            displayedCouriers.byId[shift.courierId]?.fullName?.toLowerCase();
          return courierName?.includes(nameSearch);
        });
      });
      filtered = filteredByName;
    }

    if (selectedShiftStatus !== ShiftOverviewStatus.ALL) {
      const filteredByStatus: ShiftsByDate = {};
      Object.entries(filtered).forEach(([date, shifts]) => {
        filteredByStatus[date] = shifts.filter(
          shift =>
            shift.status === selectedShiftStatus.toUpperCase().replace(' ', '_')
        );
      });
      filtered = filteredByStatus;
    }

    if (selectedCarType !== CourierVehicleType.ALL) {
      const filteredByVehicle: ShiftsByDate = {};
      Object.entries(filtered).forEach(([date, shifts]) => {
        filteredByVehicle[date] = shifts.filter(
          shift =>
            displayedCouriers.byId[shift.courierId]?.carType ===
            selectedCarType.valueOf()
        );
      });
      filtered = filteredByVehicle;
    }

    setFilteredShifts(filtered);
  }, [
    displayedShifts,
    displayedCouriers,
    nameSearch,
    selectedShiftStatus,
    selectedCarType,
  ]);

  useEffect(() => {
    fetchCouriersEfficiency()
      .then(courierEfficiency => setCouriersEfficiency(courierEfficiency))
      .catch(handleError);
  }, []);

  useEffect(() => {
    setShiftsToPublish(
      displayedShifts.currentShifts.filter(shift => !shift.published).length
    );
  }, [displayedShifts]);

  return (
    <div className="shiftPlanner">
      <div className="shiftPlanner__top">
        <div>
          <ShiftFilterOptions
            selectedJobType={selectedJobType}
            onlyExtraWork={onlyExtraWork}
            setSelectedJobType={setSelectedJobType}
            setOnlyExtraWork={setOnlyExtraWork}
            setNameSearch={setNameSearch}
            selectedShiftStatus={selectedShiftStatus}
            setSelectedShiftStatus={setSelectedShiftStatus}
            setSelectedCarType={setSelectedCarType}
            selectedCarType={selectedCarType}
          />
        </div>
        <div className="shiftPlanner__top__week">
          <PlannerDates
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            duplicateWeek={
              displayedShifts.currentShifts.length > 0 && copyToNextWeek
            }
          />
          <TransportOverview
            className="shiftPlanner__top__week__transport"
            couriers={displayedCouriers}
            shifts={displayedShifts.currentShifts}
            showAsPercentages={true}
          />
        </div>
        <div className="shiftPlanner__top__publish">
          <ButtonComponent
            disabled={shiftsToPublish === 0}
            text={
              shiftsToPublish > 0
                ? `Publish changes (${shiftsToPublish})`
                : 'Publish changes'
            }
            onClick={publishShifts}
            customColor={{ background: '#661aff', color: 'white' }}
          />
        </div>
      </div>
      {fetchStatus === ReqEnum.SUCCESS && (
        <ShiftPlannerContext.Provider
          value={{
            shiftCount: displayedShifts.shiftCount,
            startDate,
            endDate,
            daysUntilCalculation,
            region,
            couriersEfficiency,
          }}
        >
          <ShiftCalendar
            displayedCouriers={displayedCouriers}
            displayedShifts={filteredShifts}
            startDate={startDate}
            endDate={endDate}
          />
        </ShiftPlannerContext.Provider>
      )}
      {fetchStatus === ReqEnum.PENDING && <Loader size="xxl" type="spinner" />}
      {fetchStatus === ReqEnum.ERROR && null}
    </div>
  );
};

export default ShiftPlanner;
